/**
 * Checkout — the "quote" page. Extracted verbatim from App.tsx 838-1669
 * with content-driven copy from the `checkout-labels` and `categories`
 * surfaces. Visual layout preserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  earliestDeliveryDate,
  formatMoney,
  isHidden,
  resolveMinLeadDays,
} from '@brandbox/content-schema';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Upload,
  AlertCircle,
  Trash2,
  Package,
  ShoppingBag,
} from 'lucide-react';
import type {
  Cart,
  FlowConfig,
  CustomerInfo,
  LogoSelection,
  Selections,
} from '../types';
import { checkLogoSelection, MAX_LOGO_COUNT } from '@brandbox/content-schema';
import { computeUnitBoxPrice, describeBand, getActiveTier } from '../lib/pricing';
import { QuantityStepper } from './QuantityStepper';
import { resolveCtaLabels } from './checkoutCtaLabels';
import { useFlowSummaries, useLandingContent, type DefaultsSurface } from '../lib/content';
import {
  buildCategoryImageMap,
  filterCategoriesToFlowBacked,
  DEFAULT_FLOW_HERO_IMAGE,
  type FlowVisibilityProjection,
} from '../lib/categories';
import { buildPositionalSlots, type PositionalSlotItem } from '../lib/orderPipeline';
import { buildReviewLines, type ReviewableItem } from '../lib/reviewSummary';
import { SiteFooter } from './SiteFooter';
import { ReviewRequestModal, type ReviewSection } from './modals/ReviewRequestModal';

interface Props {
  cart: Cart;
  flow: FlowConfig;
  /**
   * Chapter 10 (F12) — the per-step selections, the other half of "what is in
   * this box" alongside `flow`. Needed so the inline quantity stepper can ask
   * `computeUnitBoxPrice` what a box would cost at some other quantity using
   * the SAME arithmetic that produced `cart.pricing`, rather than a second
   * answer derived from the positional projection.
   */
  selections: Selections;
  /**
   * The box's defaults surface. Supplies the quantity floor for the inline
   * stepper (F12) and the site-wide `minLeadDays` fallback for the delivery
   * date floor (F8).
   */
  defaults: DefaultsSurface;
  onUpdateQuantity: (qty: number) => void;
  onRemoveItem: (itemId: string) => void;
  onCheckout: (agreedToNewsletter: boolean) => void;
  onBack: () => void;
  onSelectCategory: (id: string) => void;
  onNavigateToStep: (stepId: string) => void;
  customerInfo: CustomerInfo;
  setCustomerInfo: (info: CustomerInfo) => void;
  deliveryDate: string;
  setDeliveryDate: (date: string) => void;
  notes: string;
  setNotes: (notes: string) => void;
  /**
   * Chapter 10 (F15) — the logos the customer has attached, in pick order. A
   * company can have several (a primary mark, a wordmark, a mono variant), so
   * this is a list and the first entry is the primary one.
   */
  logos: readonly LogoSelection[];
  setLogos: (logos: LogoSelection[]) => void;
  isSubmitting: boolean;
  /**
   * M4 (customer-logo pipeline) — true after a recoverable logo-upload
   * failure. Logos are OPTIONAL; on the first failure the order is held and
   * `error` carries a friendly message offering retry-or-proceed. When this is
   * set, the primary CTA relabels to make "send WITHOUT the logos" explicit
   * (clicking it again proceeds with no blob pointers), and a quiet
   * "remove & continue" affordance lets the customer drop the files.
   */
  logoUploadFailed?: boolean;
  error: string | null;
  // N6c — storage/shipment values are now `string` (the option lists are
  // store-ruled via the `checkout-labels` surface, so they are no longer a
  // fixed literal union). The DEFAULT value + the submitted string are
  // unchanged, so the submission/email round-trip stays byte-identical.
  storage: string;
  setStorage: (val: string) => void;
  shipment: string;
  setShipment: (val: string) => void;
  setShowConfigErrorModal: (show: boolean) => void;
}

/**
 * N6c — canonical STORAGE / SHIPMENT option lists. These are the byte-identical
 * defaults used when the `checkout-labels` surface omits the store-ruled
 * arrays (e.g. a pre-N6c Cosmos doc). The shipped `landing-checkout-labels.json`
 * carries the same values, so the live render is unchanged either way.
 */
const DEFAULT_STORAGE_OPTIONS = ['LONG TERM', 'SHORT TERM', 'DECIDE LATER'] as const;
const DEFAULT_SHIPMENT_OPTIONS = ['NL', 'EUROPE', 'OTHER', 'DECIDE LATER'] as const;

export function CheckoutPage({
  cart,
  flow,
  selections,
  // Re-destructured in Chapter 10 (F8): the surface stopped being read when the
  // quantity preset grid was removed, and the delivery-date floor needs its
  // site-wide `minLeadDays`. F12 reads its `minQuantity` again too.
  defaults,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
  onBack,
  onSelectCategory,
  customerInfo,
  setCustomerInfo,
  deliveryDate,
  setDeliveryDate,
  notes,
  setNotes,
  logos,
  setLogos,
  isSubmitting,
  logoUploadFailed = false,
  error,
  storage,
  setStorage,
  shipment,
  setShipment,
  setShowConfigErrorModal,
}: Props) {
  const labels = useLandingContent('checkout-labels');
  const allCategories = useLandingContent('categories').categories;
  const flowSummaries = useFlowSummaries();
  // Chapter 10 (F8) — delivery lead time. The box may set its own; otherwise
  // the site default, otherwise 14. `earliestDelivery` is recomputed per render
  // rather than memoised on purpose: a checkout tab left open across midnight
  // must not keep offering yesterday's floor.
  const contactEmail = useLandingContent('contact').email;
  const minLeadDays = resolveMinLeadDays(flow, defaults);
  const earliestDelivery = earliestDeliveryDate(minLeadDays);
  const categoryImageMap = React.useMemo(
    () => buildCategoryImageMap(allCategories),
    [allCategories],
  );
  // N5 owner rule (2026-06-09) — "no box tile without a backing flow",
  // applied to the whole website. The "OTHER THINGS YOU MIGHT BE
  // INTERESTED IN" grid below renders `recommendedCategories` as clickable
  // box tiles (each calls `onSelectCategory(cat.id)`), so it is a box-tile
  // surface and must honour the rule: a category whose id has no backing
  // non-hidden flow (the stale Cosmos `categories` seed carries eventkit /
  // worldcup / emergency / onboarding) must NOT render here either.
  //
  // Fallback: when the flows registry is empty (API unreachable / pre-
  // hydration — which is the case for the local visual baseline), the
  // helper returns the list unchanged, so the checkout stays byte-identical.
  const flowProjections = React.useMemo<FlowVisibilityProjection[]>(
    () =>
      flowSummaries
        .filter((s) => !isHidden(s))
        .map((s) => ({
          id: s.id,
          title: s.title,
          visibility: s.visibility as 'live' | 'coming-soon',
          ...(s.heroImage ? { heroImage: s.heroImage } : {}),
        })),
    [flowSummaries],
  );
  // The legacy CheckoutPage rendered all 6 canonical categories in the
  // "Other things you might be interested in" grid. Sorted by `order`.
  const recommendedCategories = React.useMemo(
    () =>
      [...filterCategoriesToFlowBacked(allCategories, flowProjections)].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      ),
    [allCategories, flowProjections],
  );
  // Redesign pass B (item 11) — the checkout no longer SETS the quantity
  // (the `defaults`-derived preset grid is gone). It mirrors the builder's
  // value read-only; "Edit quantity" goes back to the builder's sticky-bar
  // stepper, the single setting surface.

  // N6c — the STORAGE / SHIPMENT option buttons are store-ruled: sourced from
  // the `checkout-labels` surface (`storageOptions` / `shipmentOptions`), with
  // the canonical defaults as the fallback when the surface omits them. The
  // clicked option string is what flows into the submission/email, so the
  // round-trip stays byte-identical for the default lists.
  const storageOptions =
    labels.storageOptions && labels.storageOptions.length > 0
      ? labels.storageOptions
      : [...DEFAULT_STORAGE_OPTIONS];
  const shipmentOptions =
    labels.shipmentOptions && labels.shipmentOptions.length > 0
      ? labels.shipmentOptions
      : [...DEFAULT_SHIPMENT_OPTIONS];

  // Chapter 10 (F23) — the two CTA labels are authored on the same surface, with
  // the copy P3b shipped as the fallback. Every stored document predates the
  // fields, so the fallback is the live path until the surface is re-seeded.
  const { reviewLabel, sendLabel } = resolveCtaLabels(labels);

  const [subscribedToNewsletter, setSubscribedToNewsletter] = useState(true);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  // Chapter 10 (F10 + F11) — the review-before-send gate. The page's ONE
  // primary action opens this; only the button inside it submits.
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  // F15 — why the last pick was refused, shown next to the picker. This used to
  // be an `alert()`, which cannot be read by a screen reader in context, cannot
  // be styled, and cannot be tested.
  const [logoError, setLogoError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  // Scroll targets for the review's per-section "Change" buttons. A review the
  // customer cannot act on is just a slower submit button, so each section
  // sends them back to the fields behind it.
  const selectionRef = useRef<HTMLElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const isBoxValid =
      (!!cart.box.packagingId || !!cart.box.packagingOther) &&
      (cart.box.merchIds.length > 0 || !!cart.box.merchOther);
    if (!isBoxValid) {
      setShowConfigErrorModal(true);
    }
  }, [
    cart.box.packagingId,
    cart.box.packagingOther,
    cart.box.merchIds.length,
    cart.box.merchOther,
    setShowConfigErrorModal,
  ]);

  // Ch6 — the positional packaging/merch/final-touch slots (which option step
  // owns each + its resolved selected items + "Other…" free-text) are derived
  // ONCE by `buildPositionalSlots`, the single owner shared with ShoppingCart
  // and useConfigurator, instead of re-walking `getOptionSteps(flow)` + indexing
  // slots + re-detecting Other here. For Spring/Office this is byte-identical
  // (slot 0 = the `packaging` step, whose title uppercased is "PACKAGING", etc.).
  // For a novel box (vessel/treats/wrap) the real labels + titles resolve.
  const slots = buildPositionalSlots(flow, cart.box);
  // Step-label = the slot's step TITLE uppercased, falling back to the legacy
  // positional literal. Casing/fallback are a view concern, kept at the call
  // site (the cart drawer deliberately does NOT uppercase the same titles).
  const slotLabel = (slot: number, fallback: string) =>
    (slots[slot].title ?? fallback).toUpperCase();

  // N6b — the "Other…" line item. The owner rule pins "Other"-handling as an
  // admin-driven value: the displayed label and image must come from the
  // FLOW's own Other option (its authored `label`/`image`), NOT a hardcoded
  // "OTHER..." / Other_Dots literal. The customer's free-text still takes
  // priority for the label when present (that's their input, not content).
  // Falls back to the legacy literals ONLY if the box defines no Other option
  // at all — so Spring/Office (whose Other option authors label "OTHER..." +
  // image Other_Dots.webp) render byte-identically.
  const buildOtherItem = (
    slot: number,
    item: PositionalSlotItem,
    stepFallback: string,
    stepLabelFallback: string,
  ) => ({
    id: item.id,
    // `item.customText` is the slot's free-text only when this is the Other
    // option; `item.option?.label` is the authored label — same precedence as
    // before, with the legacy literal as the final fallback.
    label: item.customText || item.option?.label || 'OTHER...',
    description: '',
    image: item.option?.image || '/images/packaging/Other_Dots.webp',
    stepId: slots[slot].stepId ?? stepFallback,
    stepLabel: slotLabel(slot, stepLabelFallback),
    price: 0,
  });

  const slotItems = (
    slot: number,
    stepFallback: string,
    stepLabelFallback: string,
  ) =>
    slots[slot].items.map((item) =>
      item.isOther
        ? buildOtherItem(slot, item, stepFallback, stepLabelFallback)
        : {
            ...item.option,
            stepId: slots[slot].stepId ?? stepFallback,
            stepLabel: slotLabel(slot, stepLabelFallback),
          },
    );

  const selectedItems = [
    ...slotItems(0, 'packaging', 'PACKAGING'),
    ...slotItems(1, 'merch', 'MERCH'),
    ...slotItems(2, 'final-touch', 'FINAL TOUCH'),
  ].filter((item) => item && item.id);

  const storagePrice = 0;
  const shipmentPrice = 0;
  const totalEstimate = cart.pricing.total + storagePrice + shipmentPrice;

  // Chapter 10 (F11) — ONE derivation of the itemised lines, rendered twice:
  // once in "Your Selection" below and once in the review dialog. Two loops
  // would be two answers to "what does this order cost", which is the fault P3a
  // found already shipped.
  const reviewLines = buildReviewLines(
    selectedItems as unknown as ReviewableItem[],
    cart.qty,
  );

  // N6b — the box preview image in the overview sidebar. The authoritative
  // source is the admin-curated `categories` surface (keyed by the box's
  // theme id). When the admin hasn't curated an image for this box id (a
  // novel admin box that isn't in the static category list), fall back to
  // the box's OWN hero image from its loaded flow doc, then to the shared
  // admin-driven `DEFAULT_FLOW_HERO_IMAGE` — never a Spring-specific literal.
  // For Spring/Office `categoryImageMap[themeId]` always resolves, so this
  // is byte-identical to the previous render for the frozen baseline.
  const flowHeroImage = (flow as { heroImage?: unknown }).heroImage;
  const boxPreviewImage =
    categoryImageMap[cart.box.themeId] ||
    (typeof flowHeroImage === 'string' && flowHeroImage.length > 0
      ? flowHeroImage
      : DEFAULT_FLOW_HERO_IMAGE);

  const [isContactFocused, setIsContactFocused] = useState(false);
  const [isContactVisible, setIsContactVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsContactVisible(entry.isIntersecting);
      },
      { threshold: 0.2 },
    );

    if (formRef.current) {
      observer.observe(formRef.current);
    }

    return () => {
      if (formRef.current) {
        observer.unobserve(formRef.current);
      }
    };
  }, []);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isFormValid =
    customerInfo.company &&
    customerInfo.fullName &&
    customerInfo.email &&
    isValidEmail(customerInfo.email) &&
    (!!cart.box.packagingId || !!cart.box.packagingOther) &&
    (cart.box.merchIds.length > 0 || !!cart.box.merchOther);

  /**
   * Chapter 10 (F10 + F11) — the page's ONE primary action.
   *
   * The validation is exactly what the old submit button ran; the only change is
   * what happens when it passes. Before P3b, three CTAs sat on this page — one
   * scrolled to the summary and two posted the order immediately — and nothing
   * distinguished them. Now every path lands here, and a passing check opens the
   * itemised review rather than sending anything.
   */
  const handleReviewClick = () => {
    const missing: string[] = [];
    if (!customerInfo.company) missing.push('company');
    if (!customerInfo.fullName) missing.push('fullName');
    if (!customerInfo.email) {
      missing.push('email');
    } else if (!isValidEmail(customerInfo.email)) {
      missing.push('invalidEmail');
    }

    const isBoxValid =
      (!!cart.box.packagingId || !!cart.box.packagingOther) &&
      (cart.box.merchIds.length > 0 || !!cart.box.merchOther);
    if (!isBoxValid) {
      setShowConfigErrorModal(true);
      return;
    }

    setMissingFields(missing);

    if (missing.length > 0) {
      formRef.current?.scrollIntoView?.({ behavior: 'smooth' });
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 500);
      return;
    }
    setIsReviewOpen(true);
  };

  /**
   * Close the review and take the customer to the fields behind the section they
   * pressed "Change" on. Nothing is lost doing this: the review holds no state
   * of its own — every value it showed belongs to this page or to the
   * configurator above it.
   */
  const handleReviewEdit = (section: ReviewSection) => {
    setIsReviewOpen(false);
    const target =
      section === 'box'
        ? selectionRef.current
        : section === 'details'
          ? detailsRef.current
          : formRef.current;
    target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  /**
   * Accept one or more logos (F15).
   *
   * Picked files are ADDED to what is already attached: a customer who comes
   * back for their wordmark must not lose their primary mark. The whole
   * resulting set is then checked, so "four attached plus two picked" is caught
   * as six even though neither number is.
   *
   * Rejection is all-or-nothing, matching the upload endpoint: attaching the
   * good half of a pick while explaining the bad half is how a customer ends up
   * sending artwork they did not mean to send.
   */
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    // Let the same file be re-picked after an error (the input keeps its value
    // otherwise and the change event never fires again).
    e.target.value = '';
    if (picked.length === 0) return;
    setLogoError(null);

    // S2 fix (M+2 hardener): magic-byte sniff before we accept the upload.
    // The browser's `accept` attribute is advisory only — a user can pick any
    // byte string and rename it `.png`. F15 narrows the accepted set to the
    // three types the upload endpoint stores (PNG/JPEG/WebP): a GIF used to
    // pass here and then 415 at submit, which now costs the customer EVERY
    // logo on the order rather than just that one.
    const { sniffLogoFile } = await import('../lib/imageMime');
    const safeFiles: File[] = [];
    for (const file of picked) {
      const { mime, safeName } = await sniffLogoFile(file);
      if (mime !== 'image/png' && mime !== 'image/jpeg' && mime !== 'image/webp') {
        setLogoError(
          `"${file.name}" is not a PNG, JPEG or WebP image. Pick one of those, ` +
            'or email the artwork to your contact.',
        );
        return;
      }
      // Wrap into a new File with the sanitized filename so anything
      // downstream that reads `.name` sees the safe form.
      safeFiles.push(new File([file], safeName, { type: mime }));
    }

    const next = [
      ...logos,
      ...safeFiles.map((file) => ({ file, preview: null })),
    ];
    // The same limits the endpoint enforces (count, per-logo bytes, total
    // bytes). Hearing about a broken selection now costs nothing; hearing about
    // it at submit time costs a rate-limit token and every attached logo.
    const check = checkLogoSelection(next.map((l) => l.file));
    if (!check.ok) {
      setLogoError(check.message);
      return;
    }
    setLogos(next);
  };

  /** Drop one logo, keeping the rest attached. */
  const removeLogo = (index: number) => {
    setLogoError(null);
    setLogos(logos.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-[#fcfcfc] font-century relative overflow-hidden">
      {/* Redesign pass C (item 17) — no background spheres on the quote
          page; the motif is reserved for landing hero + success screen.
          Item 16 — page padding moved off the root so the shared footer
          can span the full width below. */}
      <div className="py-8 px-4 md:py-12 md:px-8">
      <div className="max-w-[90%] mx-auto relative z-10">
        <div className="absolute -top-10 -right-4 md:right-0 w-32 h-32 md:w-48 md:h-48 z-20 pointer-events-none select-none">
          <img
            src="/images/logos/proms_logo.png"
            alt="DDG Promotions"
            className="w-full h-full object-contain opacity-60 md:opacity-100 transition-opacity"
          />
        </div>

        <button
          onClick={onBack}
          className="flex items-center gap-2 text-[#444444] font-century text-base font-bold tracking-widest mb-8 hover:underline transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
          BACK
        </button>

        <div className="flex justify-between items-start mb-8 md:mb-12">
          <div className="space-y-2">
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-black text-[#444444] tracking-tight flex items-baseline gap-2">
              {labels.headerTitle}
              <span className="w-3 h-3 md:w-6 md:h-6 gradient-dot" />
            </h1>
            <p className="text-[#444444] text-base md:text-lg opacity-80">
              {labels.headerSubtitle}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-12">
            <section className="space-y-6">
              <div className="flex items-center justify-between px-2">
                <h2 className="text-2xl font-black text-[#444444] uppercase tracking-tight flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-[#356d4e] text-white flex items-center justify-center text-sm">
                    1
                  </span>
                  {labels.quantityHeading}
                </h2>
                {(() => {
                  // Chapter 10 (F4): never the raw key — stored `250+` is the
                  // 250-499 band.
                  const tier = getActiveTier(cart.qty);
                  return tier === null ? null : (
                    <div className="bg-[#57c858]/10 px-3 py-1 rounded-lg border border-[#57c858]/20">
                      <span className="text-[13px] font-black text-[#356d4e] uppercase tracking-widest">
                        Tier: <span className="text-[#57c858]">{describeBand(tier)}</span>
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Chapter 10 (F12) — quantity is now set HERE as well as in the
                  builder. Redesign pass B item 11 had made this a read-only
                  mirror with an "Edit quantity" link back to the builder, on a
                  single-setting-surface argument; the owner asked for it on the
                  quote and that decision is reversed. The STATE owner is
                  unchanged: this is the builder's own `QuantityStepper`, driving
                  the same `updateQuantity`, reading its value back off the prop.
                  Nothing here holds a quantity of its own. */}
              <div
                data-testid="checkout-quantity"
                className="bg-white p-6 sm:p-8 rounded-[32px] shadow-sm border-2 border-[#356d4e] flex flex-col sm:flex-row sm:items-center justify-between gap-6"
              >
                <QuantityStepper
                  qty={cart.qty}
                  floor={defaults.minQuantity}
                  onChange={onUpdateQuantity}
                  /* Same arithmetic that produced `cart.pricing`, so the
                     stepper's saving hint cannot contradict the prices printed
                     beside it — and stays silent on a flat-priced box. */
                  unitPriceAt={(atQty) => computeUnitBoxPrice(flow, selections, atQty)}
                />
                <div className="flex flex-col gap-1 sm:text-right">
                  <p className="text-sm font-bold text-[#444444]">
                    {formatMoney(cart.pricing.unit, cart.pricing.currency)} / box ·{' '}
                    {formatMoney(cart.pricing.total, cart.pricing.currency)} total
                  </p>
                  <p className="text-xs font-bold text-gray-400">
                    Pricing tiers apply automatically
                  </p>
                </div>
              </div>
            </section>

            {(() => {
              // Same one derivation of "can the customer have this yet" as the
              // line list and the review.
              const unavailableItems = reviewLines.filter((line) => line.unavailable);
              if (unavailableItems.length > 0) {
                return (
                  <div className="p-6 bg-red-50 border-2 border-red-200 rounded-[32px] space-y-4">
                    <div className="flex items-center gap-3 text-red-600">
                      <AlertCircle className="w-6 h-6" />
                      <h4 className="font-black uppercase tracking-widest text-sm">
                        Action Required: Unavailable Items
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {unavailableItems.map((it) => {
                        return (
                          <div
                            key={it.id}
                            className="bg-white p-4 rounded-2xl shadow-sm border border-red-100 flex flex-col gap-3"
                          >
                            <div className="flex justify-between items-start">
                              <div className="min-w-0">
                                <p className="font-black text-[#356d4e] text-sm truncate">
                                  {it.label}
                                </p>
                                <p className="text-[13px] font-bold text-red-500 mt-1">
                                  Min. {it.minBoxes} boxes required
                                </p>
                              </div>
                              <button
                                onClick={() => onRemoveItem(it.id)}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <button
                              onClick={() => onUpdateQuantity(it.minBoxes ?? 0)}
                              className="w-full py-2 bg-[#356d4e] text-white text-xs font-black uppercase tracking-widest rounded-lg hover:brightness-110 transition-all"
                            >
                              Increase to {it.minBoxes}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            <section className="space-y-6" ref={selectionRef}>
              <div className="flex items-center justify-between px-2">
                <h2 className="text-2xl font-black text-[#444444] uppercase tracking-tight flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-[#356d4e] text-white flex items-center justify-center text-sm">
                    2
                  </span>
                  Your Selection
                </h2>
                <button
                  onClick={onBack}
                  className="text-xs font-black text-[#356d4e] uppercase tracking-widest hover:underline flex items-center gap-1"
                >
                  <ChevronLeft className="w-3 h-3" />
                  Add more items
                </button>
              </div>

              <div className="bg-white rounded-[32px] shadow-sm border border-gray-100 overflow-hidden">
                <div className="divide-y divide-gray-50">
                  {/* Chapter 10 (F11) — the SAME lines the review dialog renders,
                      so the page and the review cannot quote different prices
                      for one order. */}
                  {reviewLines.map((it) => {
                    const isUnavailable = it.unavailable;
                    const pricePerBox = it.unitPrice;
                    const lineTotal = it.lineTotal;

                    return (
                      <div
                        key={it.id}
                        className={`p-4 sm:p-6 flex items-center gap-4 sm:gap-6 transition-colors ${
                          isUnavailable ? 'bg-red-50/30' : 'hover:bg-gray-50/50'
                        }`}
                      >
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl overflow-hidden bg-gray-50 flex-shrink-0 border border-gray-100">
                          <img
                            src={it.image}
                            alt={it.label}
                            className={`w-full h-full object-cover ${isUnavailable ? 'grayscale opacity-50' : ''}`}
                            referrerPolicy="no-referrer"
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h4
                              className={`font-black text-sm sm:text-base uppercase tracking-tight truncate ${
                                isUnavailable ? 'text-gray-400' : 'text-[#356d4e]'
                              }`}
                            >
                              {it.label}
                            </h4>
                            <div className="text-right flex-shrink-0">
                              <p
                                className={`font-black text-sm sm:text-base ${
                                  isUnavailable ? 'text-gray-300 line-through' : 'text-[#356d4e]'
                                }`}
                              >
                                {formatMoney(lineTotal, cart.pricing.currency)}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between mt-1">
                            <div className="flex items-center gap-3">
                              <p className="text-sm font-bold text-gray-500">
                                {isUnavailable
                                  ? 'Unavailable'
                                  : `${formatMoney(pricePerBox, cart.pricing.currency)} / box`}
                              </p>
                              <button
                                onClick={() => onRemoveItem(it.id)}
                                className="text-gray-300 hover:text-red-500 transition-colors p-1"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            {isUnavailable && (
                              <p className="text-[13px] font-black text-red-500 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Min. {it.minBoxes}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            <div
              ref={detailsRef}
              className="space-y-12 bg-gray-50/50 p-6 sm:p-10 rounded-[40px] border border-gray-100"
            >
              <div className="flex items-center gap-3 px-2">
                <h2 className="text-2xl font-black text-[#444444] uppercase tracking-tight flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-[#356d4e] text-white flex items-center justify-center text-sm">
                    3
                  </span>
                  Order Details
                </h2>
              </div>

              <div className="space-y-10">
                <section className="space-y-6">
                  <div className="space-y-1">
                    <h3 className="text-xl font-black text-[#444444] uppercase tracking-tight">
                      {labels.storageHeading}
                    </h3>
                    <p className="text-xs text-[#444444] opacity-60 font-bold">
                      Store your items, boxes and event material with us
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {storageOptions.map((option) => (
                      <button
                        key={option}
                        onClick={() => setStorage(option)}
                        className={`py-5 rounded-2xl font-bold transition-all border-2 text-sm ${
                          storage === option
                            ? 'bg-[#356d4e] text-white border-[#356d4e] shadow-lg'
                            : 'bg-white text-[#356d4e] border-gray-200 hover:border-[#356d4e]/30'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </section>

                <section className="space-y-6">
                  <div className="space-y-1">
                    <h3 className="text-xl font-black text-[#444444] uppercase tracking-tight">
                      {labels.shipmentHeading}
                    </h3>
                    <p className="text-xs text-[#444444] opacity-60 font-bold">
                      Where would you like your items to go
                    </p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {shipmentOptions.map((option) => (
                      <button
                        key={option}
                        onClick={() => setShipment(option)}
                        className={`py-5 rounded-2xl font-bold transition-all border-2 text-sm ${
                          shipment === option
                            ? 'bg-[#356d4e] text-white border-[#356d4e] shadow-lg'
                            : 'bg-white text-[#356d4e] border-gray-200 hover:border-[#356d4e]/30'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </section>

                <section className="space-y-8 pt-8 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                        Logo Upload
                      </p>
                      <label className="flex items-center justify-center gap-4 w-full py-8 border-2 border-dashed border-[#356d4e]/30 rounded-2xl cursor-pointer hover:bg-[#356d4e]/5 transition-colors bg-white">
                        <Upload className="w-8 h-8 text-[#356d4e]" />
                        {/* F15 — the label counts what is attached; the names
                            themselves are listed below, because several
                            filenames do not fit on a button. A single logo
                            still shows its own name, as before. */}
                        <span
                          className={
                            logos.length > 0
                              ? 'font-bold text-[#356d4e] text-[13px]'
                              : 'font-bold text-[#356d4e] uppercase tracking-widest text-[13px]'
                          }
                        >
                          {logos.length === 0
                            ? labels.logoUploadLabel
                            : logos.length === 1
                              ? logos[0]!.file.name
                              : `${logos.length} logos attached`}
                        </span>
                        <input
                          type="file"
                          data-testid="logo-input"
                          className="hidden"
                          // F15 — a company can have more than one logo.
                          multiple
                          // Narrowed from `image/*` to what the endpoint stores.
                          accept="image/png,image/jpeg,image/webp"
                          onChange={handleLogoUpload}
                        />
                      </label>
                      {logos.length > 0 && (
                        <ul data-testid="logo-list" className="space-y-1.5">
                          {logos.map((selected, index) => (
                            <li
                              key={`${selected.file.name}-${index}`}
                              className="flex items-center justify-between gap-3 text-[13px]"
                            >
                              <span className="truncate font-bold text-[#444444]">
                                {selected.file.name}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeLogo(index)}
                                aria-label={`Remove ${selected.file.name}`}
                                className="shrink-0 text-[11px] font-black uppercase tracking-widest text-gray-400 hover:text-[#356d4e] underline"
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {logoError && (
                        <p
                          data-testid="logo-error"
                          role="alert"
                          className="text-xs font-bold text-rose-700"
                        >
                          {logoError}
                        </p>
                      )}
                      <p className="text-xs font-bold text-gray-400">
                        PNG, JPEG or WebP · up to {MAX_LOGO_COUNT} logos, 2 MB each.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                        {labels.deliveryDateLabel}
                      </p>
                      <input
                        type="date"
                        value={deliveryDate}
                        min={earliestDelivery}
                        onChange={(e) => setDeliveryDate(e.target.value)}
                        aria-describedby="delivery-lead-time-note"
                        className="w-full py-8 px-6 border-2 border-dashed border-[#356d4e]/30 rounded-2xl font-bold text-[#356d4e] tracking-widest outline-none focus:bg-[#356d4e]/5 transition-colors text-center bg-white text-sm"
                      />
                      {/* Chapter 10 (F8) — a client could ask for tomorrow. The
                          floor is enforced again server-side; `min` alone is
                          only a hint to the picker. */}
                      <p
                        id="delivery-lead-time-note"
                        className="text-xs font-bold text-gray-400 text-center"
                      >
                        We need at least {minLeadDays} days. Need it sooner?{' '}
                        <a
                          href={`mailto:${contactEmail}`}
                          className="text-[#356d4e] underline underline-offset-2"
                        >
                          Contact us
                        </a>
                        .
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                      {labels.notesLabel}
                    </p>
                    <textarea
                      placeholder={labels.notesPlaceholder}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full py-4 px-0 border-0 border-b-2 border-[#57c858] bg-transparent outline-none focus:ring-0 resize-none min-h-[80px] text-[#444444] placeholder:text-gray-300 text-sm"
                    />
                  </div>
                </section>
              </div>
            </div>

            <section
              ref={formRef}
              className={`space-y-8 p-6 sm:p-10 rounded-[40px] shadow-sm border transition-all duration-500 ${
                isContactFocused
                  ? 'bg-white border-[#356d4e] ring-4 ring-[#356d4e]/5 shadow-2xl scale-[1.01]'
                  : 'bg-white border-gray-100'
              }`}
              onFocus={() => setIsContactFocused(true)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setIsContactFocused(false);
                }
              }}
            >
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-black text-[#444444] uppercase tracking-tight flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-[#356d4e] text-white flex items-center justify-center text-sm">
                    4
                  </span>
                  Contact Details
                </h2>
                {isFormValid && (
                  <div className="flex items-center gap-2 text-[#57c858] animate-bounce">
                    <Check className="w-5 h-5" />
                    <span className="text-xs font-black">
                      Ready to go!
                    </span>
                  </div>
                )}
              </div>

              {/* Redesign pass B (item 12) — persistent labels (never
                  placeholder-only) with explicit required markers, wired via
                  htmlFor/id for assistive tech. */}
              <div className="space-y-8">
                <div className="relative">
                  <label
                    htmlFor="checkout-company"
                    className="block text-[11px] font-black text-gray-400 uppercase tracking-widest"
                  >
                    {labels.companyPlaceholder}
                    <span className="text-red-500 ml-1" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    id="checkout-company"
                    ref={firstInputRef}
                    type="text"
                    required
                    aria-required="true"
                    value={customerInfo.company || ''}
                    onChange={(e) => {
                      setCustomerInfo({ ...customerInfo, company: e.target.value });
                      if (e.target.value)
                        setMissingFields((prev) => prev.filter((f) => f !== 'company'));
                    }}
                    className={`w-full py-4 px-0 border-0 border-b-2 bg-transparent outline-none focus:ring-0 text-[#444444] text-lg font-bold transition-colors ${
                      missingFields.includes('company') ? 'border-red-500' : 'border-[#57c858]'
                    }`}
                  />
                  {customerInfo.company && (
                    <Check className="absolute right-0 bottom-4 w-5 h-5 text-[#57c858]" />
                  )}
                  {missingFields.includes('company') && (
                    <p className="text-xs font-bold text-red-500 mt-1">
                      Company is required
                    </p>
                  )}
                </div>

                <div className="relative">
                  <label
                    htmlFor="checkout-fullname"
                    className="block text-[11px] font-black text-gray-400 uppercase tracking-widest"
                  >
                    {labels.fullNamePlaceholder}
                    <span className="text-red-500 ml-1" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    id="checkout-fullname"
                    type="text"
                    required
                    aria-required="true"
                    value={customerInfo.fullName || ''}
                    onChange={(e) => {
                      setCustomerInfo({ ...customerInfo, fullName: e.target.value });
                      if (e.target.value)
                        setMissingFields((prev) => prev.filter((f) => f !== 'fullName'));
                    }}
                    className={`w-full py-4 px-0 border-0 border-b-2 bg-transparent outline-none focus:ring-0 text-[#444444] text-lg font-bold transition-colors ${
                      missingFields.includes('fullName') ? 'border-red-500' : 'border-[#57c858]'
                    }`}
                  />
                  {customerInfo.fullName && (
                    <Check className="absolute right-0 bottom-4 w-5 h-5 text-[#57c858]" />
                  )}
                  {missingFields.includes('fullName') && (
                    <p className="text-xs font-bold text-red-500 mt-1">
                      Full name is required
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-1 relative">
                    <label
                      htmlFor="checkout-email"
                      className="block text-[11px] font-black text-gray-400 uppercase tracking-widest"
                    >
                      {labels.emailPlaceholder}
                      <span className="text-red-500 ml-1" aria-hidden="true">
                        *
                      </span>
                    </label>
                    <input
                      id="checkout-email"
                      type="email"
                      required
                      aria-required="true"
                      value={customerInfo.email || ''}
                      onChange={(e) => {
                        setCustomerInfo({ ...customerInfo, email: e.target.value });
                        if (e.target.value)
                          setMissingFields((prev) =>
                            prev.filter((f) => f !== 'email' && f !== 'invalidEmail'),
                          );
                      }}
                      className={`w-full py-4 px-0 border-0 border-b-2 bg-transparent outline-none focus:ring-0 text-[#444444] text-lg font-bold transition-colors ${
                        missingFields.includes('email') || missingFields.includes('invalidEmail')
                          ? 'border-red-500'
                          : 'border-[#57c858]'
                      }`}
                    />
                    {customerInfo.email && isValidEmail(customerInfo.email) && (
                      <Check className="absolute right-0 bottom-4 w-5 h-5 text-[#57c858]" />
                    )}
                    {missingFields.includes('email') && (
                      <p className="text-xs font-bold text-red-500 mt-1">
                        Email is required
                      </p>
                    )}
                    {missingFields.includes('invalidEmail') && (
                      <p className="text-xs font-bold text-red-500 mt-1">
                        Please enter a valid email address
                      </p>
                    )}
                  </div>
                  <div className="relative">
                    <label
                      htmlFor="checkout-phone"
                      className="block text-[11px] font-black text-gray-400 uppercase tracking-widest"
                    >
                      {labels.phonePlaceholder}
                    </label>
                    <input
                      id="checkout-phone"
                      type="tel"
                      value={customerInfo.phone || ''}
                      onChange={(e) =>
                        setCustomerInfo({ ...customerInfo, phone: e.target.value })
                      }
                      className="w-full py-4 px-0 border-0 border-b-2 border-[#57c858] bg-transparent outline-none focus:ring-0 text-[#444444] text-lg font-bold"
                    />
                    {customerInfo.phone && (
                      <Check className="absolute right-0 bottom-4 w-5 h-5 text-[#57c858]" />
                    )}
                  </div>
                </div>

                {/* Chapter 10 (F10) — the third CTA is gone. It existed only to
                    scroll the page to the sticky summary, which is the scroll
                    dance the feedback describes; with a primary action in view at
                    every breakpoint there is nothing left to scroll to. What
                    remains is the reassurance, plus a plain statement of what the
                    button actually does — the surprise F11 reports. */}
                <div className="pt-8 space-y-2 text-center">
                  <p className="text-xs font-bold text-[#356d4e]">
                    You&apos;ll see a full summary to check before anything is sent.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-gray-400">
                    <AlertCircle className="w-4 h-4" />
                    <p className="text-xs font-bold">
                      Our team will review your request and send a proposal within 24 hours
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="lg:col-span-5">
            <div
              ref={summaryRef}
              className="lg:sticky lg:top-8 bg-[#356d4e] rounded-[40px] p-8 text-white shadow-xl space-y-8 scroll-mt-8"
            >
              <div className="flex justify-between items-center border-b border-white/20 pb-6">
                <h2 className="text-3xl font-black tracking-tight uppercase">
                  {labels.overviewLabel}
                </h2>
                <div className="w-20 h-20 flex items-center justify-center">
                  <img
                    src="/images/logos/proms_logo.png"
                    alt="DDG Promotions"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              <div className="w-full aspect-video rounded-2xl overflow-hidden border-2 border-white/20 shadow-sm">
                <img
                  src={boxPreviewImage}
                  alt="Box Preview"
                  width={400}
                  height={225}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <p className="text-[11px] font-black uppercase tracking-[0.2em] text-white/60">
                    {labels.estimatedTotalLabel}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-white">
                      {formatMoney(totalEstimate, cart.pricing.currency)}
                    </span>
                    <span className="text-sm font-bold text-white/60">
                      {labels.vatSuffix}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 py-6 border-y border-white/20">
                  <div className="space-y-1">
                    <p className="text-[11px] font-black uppercase tracking-widest text-white/60">
                      Price per box
                    </p>
                    <p className="text-xl font-black text-white">
                      {formatMoney(cart.pricing.unit, cart.pricing.currency)}
                    </p>
                  </div>
                  <div className="space-y-1 text-right">
                    <p className="text-[11px] font-black uppercase tracking-widest text-white/60">
                      Quantity
                    </p>
                    <p className="text-xl font-black text-white">{cart.qty} Boxes</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-[#57c858]" />
                      <span className="text-[11px] font-black uppercase tracking-widest text-white/60">
                        Storage
                      </span>
                    </div>
                    {/* Value is content-driven; no CSS uppercase so admin-supplied
                        sentence-case options render as authored. */}
                    <span className="text-[13px] font-black text-white">{storage}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <ShoppingBag className="w-4 h-4 text-[#57c858]" />
                      <span className="text-[11px] font-black uppercase tracking-widest text-white/60">
                        Shipment
                      </span>
                    </div>
                    <span className="text-[13px] font-black text-white">{shipment}</span>
                  </div>
                </div>

                <div className="p-4 bg-white/10 rounded-2xl border border-white/5">
                  <p className="text-xs font-bold text-white/60 italic leading-relaxed">
                    * Final pricing will be confirmed after review of your branding requirements
                    and delivery details.
                  </p>
                </div>
              </div>

              <div className="space-y-4 pt-4">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div
                    onClick={() => setSubscribedToNewsletter(!subscribedToNewsletter)}
                    className={`w-6 h-6 rounded-full border-2 transition-all flex items-center justify-center ${
                      subscribedToNewsletter
                        ? 'bg-[#57c858] border-[#57c858]'
                        : 'border-white/30 group-hover:border-[#57c858]'
                    }`}
                  >
                    {subscribedToNewsletter && (
                      <Check className="w-4 h-4 text-white stroke-[3px]" />
                    )}
                  </div>
                  <span className="text-xs font-bold text-white">{labels.newsletterLabel}</span>
                </label>

                {/* Chapter 10 (F10 + F11) — the ONE primary action at lg and up,
                    where this panel is sticky and therefore always in view. It
                    opens the review; it does not send. Hidden below lg, where the
                    fixed bottom bar carries the same action, so a customer sees
                    exactly one of them and never none.
                    Chapter 10 (F23) resolved P3b's note: the label is authored as
                    `checkout-labels.reviewLabel`. `submitLabel` stays retired —
                    this button is a navigation step, not the submit. */}
                <button
                  data-testid="review-cta-desktop"
                  onClick={handleReviewClick}
                  disabled={isSubmitting}
                  className={`hidden lg:flex w-full items-center justify-center gap-2 py-5 rounded-[24px] font-black text-xl uppercase tracking-widest transition-all shadow-xl disabled:opacity-50 disabled:grayscale ${
                    isFormValid
                      ? 'bg-white text-[#356d4e] hover:bg-gray-100 active:scale-[0.98]'
                      : 'bg-white/20 text-white/40'
                  }`}
                >
                  {reviewLabel}
                  <ChevronRight className="w-5 h-5" />
                </button>

                {error && (
                  <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-2xl space-y-3">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-200 flex-shrink-0" />
                      <p className="text-xs font-bold text-red-100">{error}</p>
                    </div>
                    {/* M4 — recoverable logo failure: a quiet "remove & retry"
                        affordance. Dropping the files clears the failure flag so
                        the next submit posts the order with NO logo (and no
                        upload attempt); re-picking retries the upload. F15 — the
                        label counts what would be dropped. */}
                    {logoUploadFailed && logos.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setLogos([])}
                        className="text-[11px] font-black uppercase tracking-widest text-white/80 hover:text-white underline underline-offset-4"
                      >
                        Remove {logos.length > 1 ? 'logos' : 'logo'} &amp; continue
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-32 space-y-12">
          <div className="space-y-2">
            <h2 className="text-4xl md:text-5xl font-black text-[#444444] tracking-tight uppercase">
              OTHER THINGS YOU MIGHT BE INTERESTED IN
            </h2>
            <div className="w-24 h-1 bg-linear-to-r from-[#356d4e] to-[#57c858] rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {recommendedCategories.map((cat) => (
              <div
                key={cat.id}
                className={`group flex flex-col items-center relative overflow-hidden rounded-3xl shadow-sm transition-all duration-500 bg-white ${
                  cat.isAvailable
                    ? 'cursor-pointer hover:shadow-xl'
                    : 'cursor-not-allowed opacity-60 grayscale-[0.5]'
                }`}
                onClick={() => cat.isAvailable && onSelectCategory(cat.id)}
                aria-disabled={!cat.isAvailable}
                title={cat.isAvailable ? undefined : 'Coming soon'}
              >
                <div className="w-full aspect-video overflow-hidden relative">
                  {!cat.isAvailable && (
                    <div className="absolute top-4 left-4 z-30">
                      <span className="bg-[#57c858] text-white text-[11px] font-black px-2 py-1 rounded-md tracking-widest uppercase shadow-lg">
                        COMING SOON
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 z-20 flex items-center justify-center">
                    <span
                      className={`text-2xl font-bold tracking-[0.1em] text-white font-century drop-shadow-lg transition-transform duration-500 uppercase ${
                        cat.isAvailable ? 'group-hover:scale-110' : ''
                      }`}
                    >
                      {cat.label}
                    </span>
                  </div>
                  <img
                    src={cat.image}
                    alt={cat.label}
                    width={400}
                    height={225}
                    loading="lazy"
                    className={`w-full h-full object-cover transition-transform duration-700 ${
                      cat.isAvailable ? 'group-hover:scale-105' : ''
                    }`}
                    referrerPolicy="no-referrer"
                  />
                  <div
                    className={`absolute inset-0 bg-black/20 transition-colors duration-500 ${
                      cat.isAvailable ? 'group-hover:bg-black/10' : ''
                    }`}
                  />
                </div>
                <div className="p-6 w-full flex justify-between items-center">
                  <span className="text-sm font-bold text-[#444444] uppercase tracking-widest">
                    {cat.isAvailable ? 'View Collection' : 'Coming Soon'}
                  </span>
                  {cat.isAvailable && (
                    <ChevronRight className="w-5 h-5 text-[#356d4e] group-hover:translate-x-1 transition-transform" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>

      {/* Redesign pass C (item 16) — shared footer; extra bottom padding
          below lg keeps the legal bar clear of the fixed mobile CTA bar. */}
      <SiteFooter className="pb-28 lg:pb-0" />

      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 p-4 shadow-[0_-10px_30px_rgba(0,0,0,0.05)] flex items-center justify-between gap-4 transition-all duration-500 ${
          isContactVisible ? 'ring-4 ring-[#356d4e]/10 bg-[#fcfcfc]' : ''
        }`}
      >
        <div className="flex flex-col">
          <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
            {labels.estimatedTotalLabel}
          </span>
          <span className="text-2xl font-black text-[#356d4e]">
            {formatMoney(totalEstimate, cart.pricing.currency)}
          </span>
        </div>
        {/* The same ONE action below lg, where this bar is the always-visible
            surface and the sticky summary panel is not sticky. */}
        <button
          data-testid="review-cta-mobile"
          onClick={handleReviewClick}
          disabled={isSubmitting}
          className={`flex-1 py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-lg transition-all ${
            isFormValid
              ? 'bg-linear-to-r from-[#356d4e] to-[#57c858] text-white shadow-[#356d4e]/20'
              : 'bg-gray-100 text-gray-400'
          } disabled:opacity-50`}
        >
          {reviewLabel}
        </button>

        {error && !isContactVisible && (
          <div className="absolute -top-12 left-4 right-4 p-2 bg-red-600 text-white text-xs font-bold rounded-lg shadow-lg flex items-center gap-2 animate-bounce">
            <AlertCircle className="w-3 h-3" />
            {error}
          </div>
        )}
      </div>

      {/* Chapter 10 (F10 + F11) — the itemised review, and the only place an
          order is actually sent. It holds no state of its own, so closing it
          returns the customer to a page with every field exactly as they left
          it. `isSubmitting` / `error` / `logoUploadFailed` are threaded in so a
          failed send is reported where the customer is standing rather than
          behind a dialog they have to dismiss to find it. */}
      <ReviewRequestModal
        open={isReviewOpen}
        onClose={() => setIsReviewOpen(false)}
        sendLabel={sendLabel}
        onSend={() => onCheckout(subscribedToNewsletter)}
        onEdit={handleReviewEdit}
        state={{ isSubmitting, logoUploadFailed, error }}
        order={{
          boxName: cart.box.boxName || String(cart.box.themeId).toUpperCase(),
          qty: cart.qty,
          bandLabel: (() => {
            const tier = getActiveTier(cart.qty);
            return tier === null ? null : describeBand(tier);
          })(),
          lines: reviewLines,
          unitPrice: cart.pricing.unit,
          total: totalEstimate,
          currency: cart.pricing.currency,
          vatSuffix: labels.vatSuffix,
          deliveryDate,
          storage,
          shipment,
          notes,
          // P3b made this an array precisely so F15 would not need the surface
          // reshaped; it now carries every attached logo.
          logoFilenames: logos.map((l) => l.file.name),
          contact: customerInfo,
        }}
      />
    </div>
  );
}
